import { useState } from 'react';
import { CalendarPlus, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConvertToJobWizard } from '@/components/leads/ConvertToJobWizard';
import { useLead } from '@/hooks/useLeads';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import type { AcceptedEstimateToBook, ToBook } from '@/hooks/useSchedule';
import type { LeadWithRelations } from '@/types';
import { ScheduleJobCard } from './ScheduleJobCard';

interface ToBookPanelProps {
  toBook: ToBook;
  timezone: string;
  colourOf: (techId: string | null) => string;
  onOpenJob: (id: string) => void;
  onOpenEstimate: (id: string) => void;
}

function DepositBadge({ estimate }: { estimate: AcceptedEstimateToBook }) {
  const { formatMoney } = useCompanyLocale();
  const owed = Number(estimate.deposit_amount ?? 0);
  if (!(owed > 0)) return null;
  const paid = Number(estimate.deposit_paid_amount ?? 0);
  return paid >= owed - 0.004
    ? <Badge variant="success">Deposit paid</Badge>
    : <Badge variant="warning">Deposit {formatMoney(owed)} unpaid</Badge>;
}

/**
 * Customer said yes, nothing booked yet. Jobs the tech quoted on site drag straight onto a lane
 * (office_schedule_job books the return visit); leads open the usual Book job wizard.
 */
export function ToBookPanel({ toBook, timezone, colourOf, onOpenJob, onOpenEstimate }: ToBookPanelProps) {
  const [open, setOpen] = useState(true);
  const { formatMoney } = useCompanyLocale();
  const count = toBook.jobs.length + toBook.leads.length;
  if (count === 0) return null;

  return (
    <div className="rounded-lg border border-success/40 bg-success/5">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium"
        aria-expanded={open}
      >
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        <CalendarPlus className="h-4 w-4 text-success" />
        To book
        <Badge variant="success">{count}</Badge>
        <span className="font-normal text-muted-foreground">Accepted quotes waiting for an appointment</span>
      </button>

      {open && (
        <div className="flex flex-wrap gap-2 px-3 pb-3">
          {toBook.jobs.map(({ job, estimate }) => (
            <div key={job.id} className="w-56 space-y-1">
              <ScheduleJobCard job={job} colour={colourOf(job.assigned_tech_id)} timezone={timezone} onOpen={onOpenJob} />
              <div className="flex flex-wrap items-center gap-1 text-[11px] text-muted-foreground">
                <button type="button" className="hover:underline" onClick={() => onOpenEstimate(estimate.id)}>
                  {estimate.estimate_number}
                </button>
                <span>· drag onto a technician</span>
                <DepositBadge estimate={estimate} />
              </div>
            </div>
          ))}

          {toBook.leads.map((estimate) => (
            <div key={estimate.id} className="w-56 space-y-1.5 rounded-md border border-l-4 border-l-success bg-card px-2 py-1.5">
              <div className="flex items-center gap-1.5 text-xs font-semibold">
                <button type="button" className="truncate hover:underline" onClick={() => onOpenEstimate(estimate.id)}>
                  {estimate.estimate_number}
                </button>
                <span className="ml-auto shrink-0 font-normal tabular-nums text-muted-foreground">{formatMoney(estimate.estimate_amount)}</span>
              </div>
              <p className="truncate text-xs">{estimate.customer_name || 'Unknown customer'}</p>
              <div className="flex items-center gap-1">
                <DepositBadge estimate={estimate} />
                <BookLeadButton leadId={estimate.lead_id} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Loads the lead on demand and opens the standard Book job wizard for it. */
function BookLeadButton({ leadId }: { leadId: string }) {
  const [requested, setRequested] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const { data: lead, isFetching } = useLead(requested ? leadId : null);

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        className="ml-auto h-7 gap-1 px-2 text-xs"
        disabled={requested && isFetching && !lead}
        onClick={() => { setRequested(true); setWizardOpen(true); }}
      >
        {requested && isFetching && !lead ? <Loader2 className="h-3 w-3 animate-spin" /> : <CalendarPlus className="h-3 w-3" />}
        Book job
      </Button>
      {lead && (
        <ConvertToJobWizard lead={lead as LeadWithRelations} open={wizardOpen} onOpenChange={setWizardOpen} />
      )}
    </>
  );
}
